import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import { styled, alpha, useTheme, type Theme } from '@mui/material/styles';
import { Box, Typography, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, Divider, TextField, CircularProgress } from '@mui/material';
import { Category } from '../../types/Category';
import { Content } from '../../types/Content';
import { cutPendingSx } from '../../utils/DocumentUtils';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { AutoFixHighDirectionIcon } from '../icons/AiSparklesIcon';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  categoryNameConflictsUnderParent,
  isDescendantCategory,
  isContentCategoryEmptyForDisplay,
  normalizePresentation,
} from '../../utils/CategoryUtils';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import { useHelpContent } from '../../utils/HelpUtils';
import {
  branchChildrenAreLoaded,
  categoryShowsBranchExpandable,
} from '../../utils/categoryLoadState';

/** Child count for drop rules; unloaded branches with children report ≥2. */
function branchDropChildCount(branch: Category): number {
  if (branchChildrenAreLoaded(branch)) {
    return branch.children?.length ?? 0;
  }
  return branch.hasChildCategories === true ? 2 : 0;
}

/** macOS Finder waits ~500ms (NSDoubleClickInterval) after a second click on the filename before inline rename. */
const FINDER_STYLE_RENAME_DELAY_MS = 500;

const TreeContainer = styled(Box)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  position: 'relative',
  zIndex: 0,
  boxSizing: 'border-box',
  overflow: 'hidden',
  minHeight: 0,
});

const treeScrollAreaSx = (theme: Theme) => ({
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  paddingTop: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: 0,
  boxSizing: 'border-box',
  '&::-webkit-scrollbar': {
    width: 8,
    height: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.action.hover,
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.divider,
    borderRadius: 4,
    '&:hover': {
      backgroundColor: theme.palette.text.disabled,
    },
  },
});

/** Branch nodes under `root` (including root when it has child categories). */
function collectSubtreeExpandableIds(root: Category): string[] {
  const ids: string[] = [];
  const walk = (c: Category) => {
    if (categoryShowsBranchExpandable(c)) {
      ids.push(c.id);
    }
    if (c.children) {
      for (const ch of c.children) {
        walk(ch);
      }
    }
  };
  walk(root);
  return ids;
}

/** `root` and every descendant category id. */
function collectSubtreeCategoryIds(root: Category): string[] {
  const ids: string[] = [root.id];
  if (root.children) {
    for (const ch of root.children) {
      ids.push(...collectSubtreeCategoryIds(ch));
    }
  }
  return ids;
}

const DropIndicator = styled('div')({
  height: '2px',
  backgroundColor: '#1976d2',
  position: 'absolute',
  left: 0,
  right: 0,
  pointerEvents: 'none',
});


interface CategoryTreeProps {
  categories: Category[];
  selectedCategoryId: string | null;
  onCategorySelect: (category: Category | null) => void;
  onCategoriesChange: (categories: Category[]) => void;
  onSplitCategory: () => void;
  onAddCategory: () => void;
  onAddContent: () => void;
  onDeleteCategory: () => void;
  onFlattenCategory: () => void;
  onCollapseSubtreeToContentCategory?: () => void;
  canCollapseSubtreeToContentCategory?: (category: Category | null) => boolean;
  onAddMatchingContent?: () => void;
  canAddMatchingContent?: (category: Category | null) => boolean;
  onCompareContent?: () => void;
  canCompareContent?: (category: Category | null) => boolean;
  onRenameCategory: (explicitCategory?: Category) => void;
  onMoveCategory: (direction: 'up' | 'down') => void;
  onCutCategory: () => void;
  onCopyCategory: () => void;
  onPasteCategory: (pasteTarget?: Category) => void;
  onImportSubtreeFromDocument: () => void;
  onDuplicateAsDocument?: () => void;
  canAddCategory: (category: Category | null) => boolean;
  canDeleteCategory: (category: Category | null) => boolean;
  canFlattenCategory: (category: Category | null) => boolean;
  canMoveCategory: (category: Category | null, direction: 'up' | 'down') => boolean;
  canCutCategory: (category: Category | null) => boolean;
  canCopyCategory: (category: Category | null) => boolean;
  canPasteCategory: (category: Category | null) => boolean;
  canImportSubtreeFromDocument: (category: Category | null) => boolean;
  canSplitCategory: boolean;
  canRenameCategory: (category: Category | null) => boolean;
  validateCategoryRenameName: (category: Category, name: string) => string;
  onCategoryInlineRenameCommit: (category: Category, newName: string) => Promise<void>;
  renameDialogOpen?: boolean;
  clipboardCategory: Category | null;
  clipboardContents: Content [];
  /** Category ids cut to the clipboard (pending paste); empty when copying. */
  cutCategoryIds?: ReadonlySet<string>;
  expandedNodes: string[];
  onNodeToggle: (nodeIds: string[]) => void;
  onDragStart?: (category: Category) => void;
  onDragOver?: (category: Category) => void;
  onDropCategory?: (sourceCategory: Category, targetCategory: Category, position: 'before' | 'after' | 'inside', copy: boolean) => void;
  onDropCategoriesIntoContent?: (
    sourceCategories: Category[],
    targetContentCategory: Category,
    copy: boolean,
  ) => void;
  onDropCategoriesIntoBranch?: (
    sourceCategories: Category[],
    targetBranch: Category,
    copy: boolean,
  ) => void;
  canDropCategoriesIntoBranch?: (
    sourceCategories: Category[],
    targetBranch: Category,
    copy: boolean,
  ) => boolean;
  onDropContents?: (contents: Content[], targetCategory: Category, position: 'before' | 'after' | 'inside', copy: boolean) => void;
  category: Category;
  onCategoryMultiSelect: (category: Category, event: React.MouseEvent) => void;
  selectedCategories:  Category[];
  onOrganizeCategories: () => void;
  canOrganizeCategories: (category: Category | null) => boolean;
  /** When false, Structure categories (AI) context menu entries are hidden (feature flag). */
  showOrganizeCategories?: boolean;
  onCategoryAiReport?: () => void;
  canCategoryAiReport?: (category: Category | null) => boolean;
  showCategoryAiReport?: boolean;
  onShowSubtree: () => void;
  /** When set, content categories with no rows for these subcases use the empty icon. */
  visibleSubcaseFilter?: string[] | null;
  /** Finder-style inline rename when clicking an already-selected category name (Settings). */
  clickToRenameInTreeEnabled?: boolean;
}

export const CategoryTree = forwardRef<HTMLDivElement, CategoryTreeProps>(({
  categories,
  selectedCategoryId,
  onCategorySelect,
  onCategoriesChange,
  onSplitCategory,
  onAddCategory,
  onAddContent,
  onDeleteCategory,
  onFlattenCategory,
  onCollapseSubtreeToContentCategory,
  canCollapseSubtreeToContentCategory,
  onAddMatchingContent,
  canAddMatchingContent,
  onCompareContent,
  canCompareContent,
  onRenameCategory,
  onMoveCategory,
  onCutCategory,
  onCopyCategory,
  onPasteCategory,
  onImportSubtreeFromDocument,
  onDuplicateAsDocument,
  canAddCategory,
  canDeleteCategory,
  canFlattenCategory,
  canMoveCategory,
  canCutCategory,
  canCopyCategory,
  canPasteCategory,
  canImportSubtreeFromDocument,
  canSplitCategory,
  canRenameCategory,
  validateCategoryRenameName,
  onCategoryInlineRenameCommit,
  renameDialogOpen = false,
  clipboardCategory,
  clipboardContents,
  cutCategoryIds = new Set(),
  expandedNodes,
  onNodeToggle,
  onDragStart,
  onDragOver,
  onDropCategory,
  onDropCategoriesIntoContent,
  onDropCategoriesIntoBranch,
  canDropCategoriesIntoBranch,
  onDropContents,
  category,
  onCategoryMultiSelect,
  selectedCategories,
  onOrganizeCategories,
  canOrganizeCategories,
  showOrganizeCategories = true,
  onCategoryAiReport,
  canCategoryAiReport,
  showCategoryAiReport = false,
  onShowSubtree,
  visibleSubcaseFilter = null,
  clickToRenameInTreeEnabled = true,
}, ref): JSX.Element => {
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    category: Category | null;
  } | null>(null);
  const [inlineRenameCategoryId, setInlineRenameCategoryId] = useState<string | null>(null);
  const [inlineRenameDraft, setInlineRenameDraft] = useState('');
  const [inlineRenameError, setInlineRenameError] = useState('');
  const renameDeferTimerRef = useRef<number | null>(null);
  const { getMenuTooltipTextForState } = useHelpContent();
  const treeExpandHint = getMenuTooltipTextForState('category-tree-expand', true);
  const treeExpandTooltipSlotProps = {
    tooltip: { sx: { pointerEvents: 'none' } },
  };
  const treeExpandIcon = useMemo(
    () => (
      <Tooltip
        title={treeExpandHint}
        placement="right"
        enterDelay={500}
        disableInteractive
        componentsProps={treeExpandTooltipSlotProps}
      >
        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
          <ChevronRightIcon fontSize="small" />
        </Box>
      </Tooltip>
    ),
    [treeExpandHint],
  );
  const treeCollapseIcon = useMemo(
    () => (
      <Tooltip
        title={treeExpandHint}
        placement="right"
        enterDelay={500}
        disableInteractive
        componentsProps={treeExpandTooltipSlotProps}
      >
        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
          <ExpandMoreIcon fontSize="small" />
        </Box>
      </Tooltip>
    ),
    [treeExpandHint],
  );

  const dragRef = useRef<string | null>(null);
  const dragOverRef = useRef<string | null>(null);

  const handleNodeSelect = (event: React.SyntheticEvent, nodeId: string) => {
    const category = findCategoryById(categories, nodeId);
    if (category) {
      onCategorySelect(category);
    }
  };

  const findCategory = (cats: Category[], id: string): Category | null => {
    if (!cats || !Array.isArray(cats) || !id) return null;

      for (const cat of cats) {
      if (cat.id === id) return cat;
        if (cat.children) {
        const found = findCategory(cat.children, id);
        if (found) return found;
        }
      }
      return null;
    };

  const findCategoryById = (cats: Category[], id: string): Category | null => {
    if (!cats || !Array.isArray(cats) || !id) return null;

    for (const cat of cats) {
      if (cat.id === id) return cat;
      if (cat.children) {
        const found = findCategoryById(cat.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const clearDeferInlineRename = () => {
    if (renameDeferTimerRef.current != null) {
      window.clearTimeout(renameDeferTimerRef.current);
      renameDeferTimerRef.current = null;
    }
  };

  const cancelInlineRename = () => {
    clearDeferInlineRename();
    setInlineRenameCategoryId(null);
    setInlineRenameDraft('');
    setInlineRenameError('');
  };

  const commitInlineRename = async (category: Category) => {
    const fresh = findCategoryById(categories, category.id) || category;
    const trimmed = inlineRenameDraft.trim();
    const err = validateCategoryRenameName(fresh, trimmed);
    if (err) {
      cancelInlineRename();
      return;
    }
    if (normalizePresentation(trimmed) === normalizePresentation(fresh.name)) {
      cancelInlineRename();
      return;
    }
    try {
      await onCategoryInlineRenameCommit(fresh, trimmed);
      cancelInlineRename();
    } catch {
      cancelInlineRename();
    }
  };

  /** Tracks last click for expand-on-double-click (native dblclick is unreliable with HTML5 drag). */
  const lastTreeRowClickRef = useRef<{ id: string; time: number } | null>(null);
  const expandedNodesRef = useRef(expandedNodes);
  expandedNodesRef.current = expandedNodes;

  const toggleBranchExpanded = (category: Category, metaKey: boolean) => {
    if (!categoryShowsBranchExpandable(category)) return;
    const current = expandedNodesRef.current;
    if (metaKey) {
      const extra = collectSubtreeExpandableIds(category);
      if (extra.length === 0) return;
      const fullyExpandedUnder = extra.every((id) => current.includes(id));
      if (fullyExpandedUnder) {
        const remove = new Set(collectSubtreeCategoryIds(category));
        onNodeToggle(current.filter((id) => !remove.has(id)));
      } else {
        onNodeToggle(Array.from(new Set([...current, ...extra])));
      }
      return;
    }
    if (current.includes(category.id)) {
      onNodeToggle(current.filter((id) => id !== category.id));
    } else {
      onNodeToggle([...current, category.id]);
    }
  };

  /** Manual double-click (native dblclick is unreliable with HTML5 drag on tree rows). */
  const tryHandleTreeDoubleClickExpand = (event: React.MouseEvent, category: Category): boolean => {
    const now = Date.now();
    const last = lastTreeRowClickRef.current;
    if (last && last.id === category.id && now - last.time < 500) {
      lastTreeRowClickRef.current = null;
      if (categoryShowsBranchExpandable(category)) {
        clearDeferInlineRename();
        toggleBranchExpanded(category, event.metaKey || event.ctrlKey);
        return true;
      }
      return false;
    }
    lastTreeRowClickRef.current = { id: category.id, time: now };
    return false;
  };

  const scheduleInlineRenameIfSelected = (category: Category) => {
    if (!clickToRenameInTreeEnabled || selectedCategoryId !== category.id || !canRenameCategory(category)) {
      return;
    }
    renameDeferTimerRef.current = window.setTimeout(() => {
      renameDeferTimerRef.current = null;
      setInlineRenameCategoryId(category.id);
      setInlineRenameDraft(category.name);
      setInlineRenameError('');
    }, FINDER_STYLE_RENAME_DELAY_MS);
  };

  const handleTreeCategoryRowClick = (event: React.MouseEvent, category: Category) => {
    event.stopPropagation();
    if (inlineRenameCategoryId === category.id) return;

    if (tryHandleTreeDoubleClickExpand(event, category)) return;

    const alreadySelected = selectedCategoryId === category.id;
    onCategorySelect(category);
    clearDeferInlineRename();

    if (alreadySelected) {
      scheduleInlineRenameIfSelected(category);
    }
  };

  const handleTreeCategoryNameClick = (event: React.MouseEvent, category: Category) => {
    event.stopPropagation();
    if (inlineRenameCategoryId === category.id) return;

    if (tryHandleTreeDoubleClickExpand(event, category)) return;

    const alreadySelected = selectedCategoryId === category.id;
    onCategorySelect(category);
    clearDeferInlineRename();

    if (alreadySelected) {
      scheduleInlineRenameIfSelected(category);
    }
  };

  useEffect(() => () => clearDeferInlineRename(), []);

  const prevSelectedCategoryIdRef = useRef<string | null>(selectedCategoryId);
  useEffect(() => {
    if (prevSelectedCategoryIdRef.current !== selectedCategoryId) {
      clearDeferInlineRename();
      prevSelectedCategoryIdRef.current = selectedCategoryId;
    }
  }, [selectedCategoryId]);

  useEffect(() => {
    if (inlineRenameCategoryId === null) return;
    if (selectedCategoryId !== inlineRenameCategoryId) {
      cancelInlineRename();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cancel only when selection leaves edited node
  }, [selectedCategoryId, inlineRenameCategoryId]);

  useEffect(() => {
    if (!clickToRenameInTreeEnabled) cancelInlineRename();
  }, [clickToRenameInTreeEnabled]);

  useEffect(() => {
    if (renameDialogOpen) cancelInlineRename();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renameDialogOpen]);

  const handleContextMenu = (event: React.MouseEvent, category: Category | null) => {
    event.preventDefault();
    cancelInlineRename();

    // Set the selected category to the right-clicked category before opening context menu
    if (category) {
      onCategorySelect(category);
    }
    
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            category,
          }
        : null
    );
  };
  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleContextMenuAction = (action: () => void) => {
    handleContextMenuClose();
    action();
  };

  const handleSelect = (event: React.MouseEvent, category: Category) => {
    event.stopPropagation();
    onCategoryMultiSelect(category, event);
  };

  const CategoryNode: React.FC<{ category: Category; level?: number }> = ({ category, level = 0 }) => {
    const theme = useTheme();
    const isSelected = selectedCategoryId === category.id;
    const branchExpandable = categoryShowsBranchExpandable(category);
    const hasLoadedChildren = (category.children?.length ?? 0) > 0;
    const isLoadingChildren =
      !category.contentCategory && category.childrenLoadState === 'loading';
    const childrenLoadFailed =
      !category.contentCategory && category.childrenLoadState === 'error';
    const needsLazyPlaceholder =
      !category.contentCategory &&
      branchExpandable &&
      !hasLoadedChildren &&
      category.childrenLoadState !== 'loaded';
    const isEmptyContentCategory = isContentCategoryEmptyForDisplay(
      category,
      visibleSubcaseFilter,
    );
    const isCutPending = cutCategoryIds.has(category.id);

    type CategoryDragItem = {
      type: 'CATEGORY';
      id: string;
    };

    type CategoryRowDragItem = {
      type: 'CATEGORY_ROW';
      categories: Category[];
    };

    type ContentRowDragItem = {
      type: 'CONTENT_ROW';
      contents: Content[];
    };

    type DragItem = CategoryDragItem | CategoryRowDragItem |ContentRowDragItem;

    const [dropTarget, setDropTarget] = useState<{
      categoryId: string;
      position: 'before' | 'after' | 'inside';
    } | null>(null);

    const [{ isDragging }, drag] = useDrag({
      type: 'CATEGORY',
      canDrag: () => category.parentId != null && category.id !== inlineRenameCategoryId,
      item: () => {
            if (
              selectedCategories.length >= 2 &&
              selectedCategories.some((c) => c.id === category.id)
            ) {
              return { type: 'CATEGORY_ROW', categories: selectedCategories };
            }
            return { type: 'CATEGORY', id: category.id };
          },
      end: () => setDropTarget(null),
      collect: (monitor) => {
          return {
            isDragging: monitor.isDragging(),
          };
      },
    });

   const ref = useRef<HTMLDivElement | null>(null);
   const [isAltPressed, setIsAltPressed] = useState(false);

   const [{ isOver, itemType }, drop] = useDrop<DragItem, void, { isOver: boolean; itemType: string | null }>({
     accept: ['CATEGORY', 'CATEGORY_ROW', 'CONTENT_ROW'],
     drop(item: DragItem, monitor: DropTargetMonitor<DragItem, void>) {
       if (!dropTarget) return;
       const targetCategory = findCategoryById(categories, dropTarget.categoryId);
       const type = monitor.getItemType();
       if (item.type === 'CATEGORY') {
            if (!dropTarget || item.id === category.id) return;
            const sourceCategory = findCategoryById(categories, item.id);

           if (sourceCategory && targetCategory) {
             onDropCategory?.(sourceCategory, targetCategory, dropTarget.position, isAltPressed);
           }
       }
       else if (item.type === 'CATEGORY_ROW') {
           if (!dropTarget || !targetCategory) return;
           if (
             dropTarget.position === 'inside' &&
             targetCategory.contentCategory &&
             item.categories.length >= 2
           ) {
             onDropCategoriesIntoContent?.(item.categories, targetCategory, isAltPressed);
             return;
           }
           if (
             dropTarget.position === 'inside' &&
             !targetCategory.contentCategory &&
             item.categories.length >= 2
           ) {
             onDropCategoriesIntoBranch?.(item.categories, targetCategory, isAltPressed);
             return;
           }
           if (item.categories.length !== 1) return;
           const sourceCategory = findCategoryById(categories, item.categories[0].id);
           if (sourceCategory && targetCategory) {
             onDropCategory?.(sourceCategory, targetCategory, dropTarget.position, isAltPressed);
           }
      }
       else if (item.type === 'CONTENT_ROW') {
           if (targetCategory) {
             onDropContents?.(item.contents, targetCategory, dropTarget.position, isAltPressed);
           }
       }

       setDropTarget(null);
     },
     canDrop(item: DragItem, monitor: DropTargetMonitor<DragItem, void>) {
       if (!dropTarget) return false;
       const targetCategory = findCategoryById(categories, dropTarget.categoryId);
       if (!targetCategory) return false;

       if ( !dropTarget.position ) return false;

       if (item.type === 'CATEGORY') {
            const sourceCategory = findCategoryById(categories, item.id);
            if (!sourceCategory || !sourceCategory.parentId) return false;

            const parentCategory = findCategoryById(categories, sourceCategory.parentId);
            if (!parentCategory) return false;

            const nameOk = !categoryNameConflictsUnderParent(
              sourceCategory.name,
              targetCategory,
              isAltPressed ? undefined : sourceCategory.id,
            );

            const canDropInside =
              dropTarget.position === 'inside' &&
              targetCategory.id !== sourceCategory.id &&
              sourceCategory.parentId !== targetCategory.id &&
              !targetCategory.contentCategory &&
              nameOk &&
              (branchDropChildCount(parentCategory) > 2 || isAltPressed) &&
              branchDropChildCount(targetCategory) > 1;

            const canReorderAmongSiblings =
              (dropTarget.position === 'before' || dropTarget.position === 'after') &&
              sourceCategory.parentId === targetCategory.parentId;

            return canDropInside || canReorderAmongSiblings;
       }
       else  if (item.type === 'CATEGORY_ROW') {
            if (item.categories.length >= 2) {
              if (dropTarget.position !== 'inside') {
                return false;
              }
              if (item.categories.some((c) => c.id === targetCategory.id)) {
                return false;
              }
              if (targetCategory.contentCategory) {
                return item.categories.every(
                  (s) =>
                    s.id !== targetCategory.id &&
                    !isDescendantCategory(s, targetCategory) &&
                    !isDescendantCategory(targetCategory, s) &&
                    !item.categories.some(
                      (other) => other.id !== s.id && isDescendantCategory(s, other),
                    ) &&
                    !categoryNameConflictsUnderParent(
                      s.name,
                      targetCategory,
                      isAltPressed ? undefined : s.id,
                    ),
                );
              }
              if (
                !canDropCategoriesIntoBranch?.(
                  item.categories,
                  targetCategory,
                  isAltPressed,
                )
              ) {
                return false;
              }
              return true;
            }
            const sourceCategory = findCategoryById(categories, item.categories[0].id);
            if (!sourceCategory || !sourceCategory.parentId) return false;

            const parentCategory = findCategoryById(categories, sourceCategory.parentId);
            if (!parentCategory) return false;

            const nameOk = !categoryNameConflictsUnderParent(
              sourceCategory.name,
              targetCategory,
              isAltPressed ? undefined : sourceCategory.id,
            );

            if (item.categories[0].contentCategory) {
              const canDropInside =
                dropTarget.position === 'inside' &&
                targetCategory.id !== sourceCategory.id &&
                sourceCategory.parentId !== targetCategory.id &&
                !targetCategory.contentCategory &&
                nameOk &&
                (branchDropChildCount(parentCategory) > 2 || isAltPressed) &&
                branchDropChildCount(targetCategory) > 1;

              const canReorderAmongSiblings =
                (dropTarget.position === 'before' || dropTarget.position === 'after') &&
                sourceCategory.parentId === targetCategory.parentId;

              return canDropInside || canReorderAmongSiblings;
            }

            const canDropInside =
              dropTarget.position === 'inside' &&
              targetCategory.id !== sourceCategory.id &&
              sourceCategory.parentId !== targetCategory.id &&
              !targetCategory.contentCategory &&
              nameOk &&
              (branchDropChildCount(parentCategory) > 2 || isAltPressed) &&
              branchDropChildCount(targetCategory) > 1;

            const canReorderAmongSiblings =
              (dropTarget.position === 'before' || dropTarget.position === 'after') &&
              sourceCategory.parentId === targetCategory.parentId;

            return canDropInside || canReorderAmongSiblings;

       }
       else if (item.type === 'CONTENT_ROW') {
           return dropTarget.position === 'inside' &&
                  targetCategory.contentCategory;
       }
       return false;
     },
     hover(item: DragItem, monitor: DropTargetMonitor<DragItem, void>) {
       if (!ref.current) return;

         const isOver = monitor.isOver({ shallow: true });
         if (!isOver) {
           setDropTarget(null);
           return;
         }

       const clientOffset = monitor.getClientOffset();
       const boundingRect = ref.current?.getBoundingClientRect();
       if (!clientOffset || !boundingRect) return;

       const y = clientOffset.y - boundingRect.top;
       const height = boundingRect.height;

       let position: 'before' | 'after' | 'inside' = 'inside';
       if (y < height / 3) position = 'before';
       else if (y > (2 * height) / 3) position = 'after';

       setDropTarget({ categoryId: category.id, position });
     },
     collect: (monitor) => ({
      isOver: monitor.isOver(),
      itemType: monitor.getItemType() as string | null,
     }),
   });

   useEffect(() => {
     if (!isOver) {
       setDropTarget((prev) => {
         // Only clear if this item owns the current dropTarget
         if (prev?.categoryId === category.id) {
           return null;
         }
         return prev;
       });
     }
   }, [isOver, category.id]);


   useEffect(() => {
     const handleKeyDown = (e: KeyboardEvent) => {
       if (e.altKey) setIsAltPressed(true);
     };
     const handleKeyUp = (e: KeyboardEvent) => {
       if (!e.altKey) setIsAltPressed(false);
     };

     window.addEventListener('keydown', handleKeyDown);
     window.addEventListener('keyup', handleKeyUp);

     return () => {
       window.removeEventListener('keydown', handleKeyDown);
       window.removeEventListener('keyup', handleKeyUp);
     };
   }, []);

    return (
      <TreeItem
        onFocusCapture={(e) => e.stopPropagation()}
        key={category.id}
        nodeId={category.id}
        data-category-id={category.id}
        label={
          <Box
              ref={(node: HTMLDivElement | null) => {
                  ref.current = node; // This assigns the ref
                  if (node) drag(drop(node)); // Attach DnD
              }}
              data-category-id={category.id}
              onClick={(e) => handleTreeCategoryRowClick(e, category)}
              onDoubleClick={(e) => {
                // Expand/collapse is handled on the second click (see handleTreeCategoryRowClick).
                // Native dblclick is suppressed for draggable rows; when it does fire (root),
                // the second click already toggled — do not toggle again.
                e.preventDefault();
                e.stopPropagation();
                lastTreeRowClickRef.current = null;
                clearDeferInlineRename();
              }}
              onContextMenu={(e) => handleContextMenu(e, category)}
              sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  minHeight: 32,
                  boxSizing: 'border-box',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                  padding: '4px 8px',
                  borderRadius: 1,
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                  borderTop:
                    dropTarget?.categoryId === category.id && dropTarget?.position === 'before'
                      ? `2px solid ${theme.palette.primary.light}`
                      : undefined,
                  borderBottom:
                    dropTarget?.categoryId === category.id && dropTarget?.position === 'after'
                      ? `2px solid ${theme.palette.primary.light}`
                      : undefined,
                  backgroundColor:
                    dropTarget?.categoryId === category.id && dropTarget?.position === 'inside'
                      ? alpha(theme.palette.primary.main, 0.1)
                      : undefined,
                  opacity: isDragging ? 0.5 : undefined,
                  ...cutPendingSx(isCutPending && !isDragging),
                }}
            >
              {category.contentCategory ? (
                isEmptyContentCategory ? (
                  <DescriptionOutlinedIcon sx={{ color: 'text.secondary', mr: 1 }} />
                ) : (
                  <DescriptionIcon sx={{ color: 'text.secondary', mr: 1 }} />
                )
              ) : branchExpandable ? (
                <FolderIcon sx={{ color: 'text.secondary', mr: 1 }} />
              ) : (
                <DescriptionIcon sx={{ color: 'text.secondary', mr: 1 }} />
              )}
              {isLoadingChildren && (
                <CircularProgress size={14} sx={{ mr: 1, flexShrink: 0 }} />
              )}
              {childrenLoadFailed && (
                <Tooltip title={category.loadError ?? 'Failed to load subcategories'}>
                  <ErrorOutlineIcon color="error" sx={{ mr: 1, flexShrink: 0, fontSize: 18 }} />
                </Tooltip>
              )}
              {inlineRenameCategoryId === category.id ? (
                <TextField
                  size="small"
                  variant="standard"
                  fullWidth
                  value={inlineRenameDraft}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const v = e.target.value;
                    const fresh = findCategoryById(categories, category.id) || category;
                    setInlineRenameDraft(v);
                    setInlineRenameError(validateCategoryRenameName(fresh, v));
                  }}
                  onBlur={() => void commitInlineRename(category)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void commitInlineRename(category);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelInlineRename();
                    }
                  }}
                  error={!!inlineRenameError}
                  helperText={inlineRenameError || undefined}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    '& .MuiInput-input': {
                      fontSize: 'var(--app-font-size, 14px)',
                      paddingTop: 2,
                      paddingBottom: 2,
                    },
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  onClick={(e) => handleTreeCategoryNameClick(e, category)}
                  sx={{
                    color: isCutPending ? 'text.secondary' : 'text.primary',
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: 'var(--app-font-size, 14px)',
                    lineHeight: 1.25,
                    flex: 1,
                    minWidth: 0,
                    fontStyle: isCutPending ? 'italic' : undefined,
                  }}
                >
                  {category.name}
                </Typography>
              )}
            </Box>
        }
      >
        {!category.contentCategory && (hasLoadedChildren || needsLazyPlaceholder) ? (
          <>
            {hasLoadedChildren &&
              category.children?.map((child) => (
                <CategoryNode key={child.id} category={child} level={level + 1} />
              ))}
            {needsLazyPlaceholder && (
              <TreeItem
                nodeId={`${category.id}__lazy`}
                label=""
                sx={{ display: 'none', height: 0, minHeight: 0, p: 0, m: 0 }}
              />
            )}
          </>
        ) : null}
      </TreeItem>
    );
  };

  const handleNodeToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    // Find the node that was toggled by comparing with previous expanded nodes
    const toggledNodeId = nodeIds.find(id => !expandedNodes.includes(id)) || 
                         expandedNodes.find(id => !nodeIds.includes(id));
    
    if (!toggledNodeId) return;

    if (expandedNodes.includes(toggledNodeId)) {
      // If the node was expanded before, it's being collapsed
      onNodeToggle(expandedNodes.filter(id => id !== toggledNodeId));
    } else {
      // If the node was collapsed before, it's being expanded
      onNodeToggle([...expandedNodes, toggledNodeId]);
    }
  };

  return (
    <>
      <TreeContainer ref={ref}>
        <Box sx={treeScrollAreaSx}>
          <TreeView
            defaultCollapseIcon={treeCollapseIcon}
            defaultExpandIcon={treeExpandIcon}
            expanded={expandedNodes}
            selected={selectedCategoryId || ''}
            onNodeSelect={handleNodeSelect}
            onNodeToggle={handleNodeToggle}
            sx={(theme) => ({
              fontSize: 'var(--app-font-size, 14px)',
              '& .MuiTreeItem-content': {
                padding: theme.spacing(0, 0.5),
                minHeight: 32,
                margin: theme.spacing(0.25, 0),
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'transparent !important',
                },
                '&.Mui-focused': {
                  backgroundColor: 'transparent !important',
                },
                '&.Mui-selected': {
                  backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
                  '&:hover': {
                    backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
                  },
                  '&.Mui-focused': {
                    backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
                  },
                },
              },
              '& .MuiTreeItem-group': {
                marginLeft: 0,
                paddingLeft: theme.spacing(2),
              },
              '& .MuiTreeItem-label': {
                paddingLeft: 0,
                paddingY: 0,
                fontSize: 'var(--app-font-size, 14px)',
              },
              '& .MuiTreeItem-iconContainer': {
                padding: 0,
                marginRight: theme.spacing(0.5),
              },
            })}
          >
            {categories.map((cat) => (
              <CategoryNode key={cat.id} category={cat} />
            ))}
          </TreeView>
        </Box>
        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <Tooltip title={getMenuTooltipTextForState('split-category', !!(contextMenu?.category && contextMenu?.category.contentCategory))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onSplitCategory();
                }}
                disabled={!contextMenu?.category || !contextMenu?.category.contentCategory}
              >
                <ListItemIcon>
                  <CallSplitIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Split Category</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          {onAddMatchingContent && (
            <Tooltip
              title={getMenuTooltipTextForState(
                'add-matching-content',
                !!(contextMenu?.category && canAddMatchingContent?.(contextMenu.category)),
              )}
              placement="right"
            >
              <span>
                <MenuItem
                  onClick={() => {
                    handleContextMenuClose();
                    onAddMatchingContent();
                  }}
                  disabled={!contextMenu?.category || !canAddMatchingContent?.(contextMenu.category)}
                >
                  <ListItemIcon>
                    <PlaylistAddOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Add matching content (AI)</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}
          {onCompareContent && (
            <Tooltip
              title={getMenuTooltipTextForState(
                'compare-content',
                !!(contextMenu?.category && canCompareContent?.(contextMenu.category)),
              )}
              placement="right"
            >
              <span>
                <MenuItem
                  onClick={() => {
                    handleContextMenuClose();
                    onCompareContent();
                  }}
                  disabled={!contextMenu?.category || !canCompareContent?.(contextMenu.category)}
                >
                  <ListItemIcon>
                    <CompareArrowsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Compare content and select</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}
          <Tooltip title={getMenuTooltipTextForState('add-category', !!(contextMenu?.category && canAddCategory(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onAddCategory();
                }}
                disabled={!contextMenu?.category || !canAddCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Category</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
           <Tooltip title={getMenuTooltipTextForState('add-content', !!(contextMenu?.category && contextMenu?.category.contentCategory))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  onAddContent?.();
                  handleContextMenuClose();
                }}
                disabled={!contextMenu?.category || !contextMenu?.category.contentCategory}
              >
                <ListItemIcon>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Content</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('delete-category', !!(contextMenu?.category && canDeleteCategory(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onDeleteCategory();
                }}
                disabled={!contextMenu?.category || !canDeleteCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete Category</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
           <Tooltip title={getMenuTooltipTextForState('flatten-category', !!(contextMenu?.category && canFlattenCategory(contextMenu.category)))} placement="right">
              <span>
                <MenuItem
                  onClick={() => {
                    handleContextMenuClose();
                    onFlattenCategory();
                  }}
                  disabled={!contextMenu?.category || !canFlattenCategory(contextMenu.category)}
                >
                  <ListItemIcon>
                    <KeyboardDoubleArrowUpIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Flatten category</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          {onCollapseSubtreeToContentCategory ? (
          <Tooltip title={getMenuTooltipTextForState(
            'collapse-subtree-to-content-category',
            !!(contextMenu?.category && canCollapseSubtreeToContentCategory?.(contextMenu.category)),
          )} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onCollapseSubtreeToContentCategory();
                }}
                disabled={!contextMenu?.category || !canCollapseSubtreeToContentCategory?.(contextMenu.category)}
              >
                <ListItemIcon>
                  <ViewListOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Collect branch content</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          ) : null}
          <Tooltip title={getMenuTooltipTextForState('rename-category', !!(contextMenu?.category && canRenameCategory(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onRenameCategory(contextMenu?.category ?? undefined);
                }}
                disabled={!contextMenu?.category || !canRenameCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <DriveFileRenameOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Rename Category</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('move-category-up', !!(contextMenu?.category && canMoveCategory(contextMenu.category, 'up')))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onMoveCategory('up');
                }}
                disabled={!contextMenu?.category || !canMoveCategory(contextMenu.category, 'up')}
              >
                <ListItemIcon>
                  <ArrowUpwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Up</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('move-category-down', !!(contextMenu?.category && canMoveCategory(contextMenu.category, 'down')))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onMoveCategory('down');
                }}
                disabled={!contextMenu?.category || !canMoveCategory(contextMenu.category, 'down')}
              >
                <ListItemIcon>
                  <ArrowDownwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Down</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('cut-category', !!(contextMenu?.category && canCutCategory(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onCutCategory();
                }}
                disabled={!contextMenu?.category || !canCutCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <ContentCutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Cut</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('copy-category', !!(contextMenu?.category && canCopyCategory(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onCopyCategory();
                }}
                disabled={!contextMenu?.category || !canCopyCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <ContentCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState(
            clipboardContents.length > 0 ? 'paste-content' : clipboardCategory ? 'paste-category' : 'paste-empty',
            !!(contextMenu?.category && canPasteCategory(contextMenu.category)),
          )} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  const pasteTarget = contextMenu?.category ?? undefined;
                  handleContextMenuClose();
                  onPasteCategory(pasteTarget);
                }}
                disabled={!contextMenu?.category || !canPasteCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <ContentPasteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Paste</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip
            title={getMenuTooltipTextForState(
              'import-subtree-from-document',
              !!(
                contextMenu?.category &&
                canImportSubtreeFromDocument(contextMenu.category)
              ),
            )}
            placement="right"
          >
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onImportSubtreeFromDocument();
                }}
                disabled={
                  !contextMenu?.category ||
                  !canImportSubtreeFromDocument(contextMenu.category)
                }
              >
                <ListItemIcon>
                  <DriveFolderUploadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Import branch from document…</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('duplicate-as-document', !!(contextMenu?.category && onDuplicateAsDocument))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuAction(() => onDuplicateAsDocument?.());
                }}
                disabled={!contextMenu?.category || !onDuplicateAsDocument}
              >
                <ListItemIcon>
                  <FileCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Duplicate as document</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          {showOrganizeCategories ? (
          <>
          <Tooltip title={getMenuTooltipTextForState('structure-categories', !!(contextMenu?.category && canOrganizeCategories(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onOrganizeCategories();
                }}
                disabled={!contextMenu?.category || !canOrganizeCategories(contextMenu.category)}
              >
                <ListItemIcon>
                  <AutoFixHighDirectionIcon direction="north" variant="menu" />
                </ListItemIcon>
                <ListItemText>Structure categories (AI)</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          </>
          ) : null}
          {showCategoryAiReport ? (
            <Tooltip
              title={getMenuTooltipTextForState(
                'category-ai-report',
                !!(contextMenu?.category && canCategoryAiReport?.(contextMenu.category)),
              )}
              placement="right"
            >
              <span>
                <MenuItem
                  onClick={() => {
                    handleContextMenuClose();
                    onCategoryAiReport?.();
                  }}
                  disabled={!contextMenu?.category || !canCategoryAiReport?.(contextMenu.category)}
                >
                  <ListItemIcon>
                    <SummarizeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Generate report (AI)…</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          ) : null}
         <Tooltip title={getMenuTooltipTextForState('show-sub-tree', !!contextMenu?.category)} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onShowSubtree();
                }}
                disabled={!contextMenu?.category}
              >
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Category Structure Overview</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>
      </TreeContainer>
    </>
  );
});

export default CategoryTree; 